import type { CollectorRun, Tone } from "./types";

export type CollectorHealth = {
  label: "Operational" | "Waiting" | "Failed" | "Stale" | "Degraded" | "Unknown";
  tone: Tone;
  detail: string;
};

export function extractionPathLabel(extraction: CollectorRun["extraction"]): string {
  if (!extraction || extraction.path === "unavailable" || extraction.total === 0) {
    return "Not reported";
  }
  if (extraction.path === "deterministic") {
    return `Deterministic · ${extraction.deterministic} accepted`;
  }
  return `${extraction.unclassified} unclassified`;
}

const STALE_AFTER_MS = 36 * 60 * 60 * 1000;

function words(value: string) {
  return value.replaceAll("_", " ");
}

export function collectorHealth(run: CollectorRun | null, now = new Date()): CollectorHealth {
  if (!run) return { label: "Unknown", tone: "neutral", detail: "awaiting first collector run" };

  const state = run.state.toLowerCase();
  if (["queued", "running"].includes(state)) {
    return { label: "Waiting", tone: "warning", detail: `collector ${words(state)}` };
  }
  if (["failed", "error"].includes(state)) {
    return { label: "Failed", tone: "danger", detail: words(run.reason_code ?? "collector run failed") };
  }
  if (!["success", "succeeded", "partial_success"].includes(state)) {
    return { label: "Unknown", tone: "neutral", detail: `unrecognized collector state: ${words(state)}` };
  }

  const finishedAt = run.finished_at ? new Date(run.finished_at) : null;
  if (!finishedAt || Number.isNaN(finishedAt.valueOf())) {
    return { label: "Unknown", tone: "neutral", detail: "run completion time not reported" };
  }

  const outcomes = Object.values(run.series ?? {});
  const unavailable = outcomes.filter((item) => ["unavailable", "quarantined"].includes(item.status)).length;
  const stale = outcomes.filter((item) => item.status === "stale").length;
  const delayed = outcomes.filter((item) => item.status === "delayed").length;
  const rejected = outcomes.reduce((total, item) => total + item.rejected, 0);

  if (state === "partial_success" || unavailable > 0 || rejected > 0 || outcomes.length === 0) {
    const detail = outcomes.length === 0
      ? "no series availability was reported"
      : state === "partial_success" && unavailable === 0 && rejected === 0
        ? "collector reported partial success"
        : `${unavailable} unavailable · ${rejected} rejected`;
    return { label: "Degraded", tone: unavailable > 0 || rejected > 0 ? "danger" : "warning", detail };
  }
  if (now.valueOf() - finishedAt.valueOf() > STALE_AFTER_MS) {
    return { label: "Stale", tone: "warning", detail: "latest collector run is older than 36 hours" };
  }
  if (stale > 0) {
    return { label: "Stale", tone: "warning", detail: `${stale} stale series` };
  }
  if (delayed > 0) {
    return { label: "Degraded", tone: "warning", detail: `${delayed} delayed series` };
  }
  return { label: "Operational", tone: "positive", detail: "latest collector run verified" };
}
